import FormFeedbackBanner from "../../components/forms/FormFeedbackBanner";
import { List, X } from "@phosphor-icons/react";
import { Link, NavLink } from "react-router-dom";
import { LanguageDropdown } from "../../localization";
import { responsiveImageProps } from "../../utils/responsiveImages";
import "./AuthPages.css";
import { AuthTopNavProps } from './authTopNavPresentation';
import { useAuthTopNavViewModel } from './useAuthTopNavViewModel';

const AuthTopNav = ({ anchorLinks = [], includeDefaultLinks = true }: AuthTopNavProps) => {
  const {
    signOutError,
    isAuthenticated,
    isAdmin,
    isAccountMenuOpen,
    setIsAccountMenuOpen,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    profilePhotoUrl,
    accountMenuRef,
    accountTriggerRef,
    handleSignOut,
    userInitials,
    linkClassName,
    linkStyle
  } = useAuthTopNavViewModel({ anchorLinks, includeDefaultLinks });

  return (
    <header className={`auth-topbar${isMobileMenuOpen ? " is-menu-open" : ""}`}>
      <div className="auth-topbar-inner">
        <Link className="auth-topbar-brand" to="/home">
          <span className="auth-topbar-brand-mark" aria-hidden="true">
            <img
              className="auth-topbar-brand-logo"
              {...responsiveImageProps("/assets/landing/rauxa-logo.png", "32px")}
              alt=""
              loading="eager"
            />
          </span>
          RAUXA
        </Link>

        <button
          className="auth-topbar-menu-toggle"
          type="button"
          aria-label={isMobileMenuOpen ? "Close navigation" : "Open navigation"}
          aria-controls="auth-topbar-primary-navigation"
          aria-expanded={isMobileMenuOpen}
          onClick={() => setIsMobileMenuOpen((current) => !current)}
        >
          {isMobileMenuOpen ? (
            <X size={23} weight="bold" aria-hidden="true" />
          ) : (
            <List size={23} weight="bold" aria-hidden="true" />
          )}
        </button>

        <nav
          id="auth-topbar-primary-navigation"
          className="auth-topbar-links"
          aria-label="Primary"
        >
          {includeDefaultLinks ? (
            <>
              <NavLink className={linkClassName} style={linkStyle} to="/home">
                Home
              </NavLink>
              <NavLink className={linkClassName} style={linkStyle} to="/discover">
                Discover
              </NavLink>
              <NavLink className={linkClassName} style={linkStyle} to="/live-racers">
                Live Racers
              </NavLink>
              <NavLink className={linkClassName} style={linkStyle} to="/organize">
                Organize a Race
              </NavLink>
              {isAuthenticated ? (
                <NavLink className={linkClassName} style={linkStyle} to="/races">
                  My Races
                </NavLink>
              ) : null}
            </>
          ) : null}
          {anchorLinks.map((link) => (
            <a key={link.href} className="auth-topbar-link" href={link.href}>
              {link.label}
            </a>
          ))}
        </nav>

        <div className="auth-topbar-actions">
          {!isAuthenticated ? (
            <>
              <LanguageDropdown className="auth-topbar-language-menu" />
              <NavLink className="auth-topbar-action auth-topbar-action-muted" to="/login">
                Log In
              </NavLink>
              <NavLink className="auth-topbar-action auth-topbar-action-primary" to="/signup">
                Sign Up
              </NavLink>
            </>
          ) : (
            <>
              <NavLink className="auth-topbar-profile-cta" to="/profile" aria-label="Open profile">
                {profilePhotoUrl ? <img src={profilePhotoUrl} alt="Profile" /> : <span>{userInitials}</span>}
              </NavLink>

              <div className="auth-topbar-account-menu" ref={accountMenuRef}>
                <button
                  ref={accountTriggerRef}
                  className="auth-topbar-action auth-topbar-action-primary auth-topbar-account-trigger"
                  type="button"
                  onClick={() => setIsAccountMenuOpen((current) => !current)}
                  aria-expanded={isAccountMenuOpen}
                  aria-controls="auth-topbar-account-options"
                >
                  Account
                </button>

                <div
                  id="auth-topbar-account-options"
                  className={`auth-topbar-account-dropdown${isAccountMenuOpen ? " is-open" : ""}`}
                  aria-label="Account menu"
                  hidden={!isAccountMenuOpen}
                >
                  <NavLink
                    className="auth-topbar-account-item"
                    to="/account"
                    onClick={() => setIsAccountMenuOpen(false)}
                  >
                    Account
                  </NavLink>
                  <button
                    className="auth-topbar-account-item auth-topbar-account-item-danger"
                    type="button"
                    onClick={() => {
                      setIsAccountMenuOpen(false);
                      void handleSignOut();
                    }}
                  >
                    Sign Out
                  </button>
                </div>
              </div>

              <LanguageDropdown className="auth-topbar-language-menu" />

              <NavLink className="auth-topbar-action auth-topbar-action-muted" to="/settings">
                Settings
              </NavLink>

              {isAdmin ? (
                <NavLink className="auth-topbar-action auth-topbar-action-primary" to="/admin">
                  Admin
                </NavLink>
              ) : null}
            </>
          )}
        </div>
      </div>
      {signOutError ? <FormFeedbackBanner tone="error" messageKey={signOutError}>{signOutError}</FormFeedbackBanner> : null}
    </header>
  );
};

export default AuthTopNav;
